import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RouteProp, useFocusEffect, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { appointmentKindsSelector, userSelector } from '@src/selectors/app'
import { StyleService, useStyleSheet } from '@src/style/service'
import {
  AppStackParamList,
  CommonAuthorizedStackParamList,
  OnboardingStackParamsList,
} from '@src/navigation/types'
import NavigationContainer from '@src/screens/Common/containers/NavigationContainer'
import {
  BillingProduct,
  CoachesCoachingKind,
  EhrAppointmentAttendanceChargeKind,
  EhrAppointmentKind,
  EhrAppointmentRecurrenceCadence,
  EhrSlot,
} from '@src/types.ts'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { CustomEventTypes, ErrorMessages } from '@src/config'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { NavigatorContext, useSnack } from '@src/utils/navigatorContext'
import { useDispatchAsync } from '@src/utils/dispatch'
import { useActionBottomSheet } from '@src/components/ActionBottomSheet'
import { isAppointmentRescheduled } from '../utils'
import { useEhrAvailableDates, useEhrAvailableSlots, useNutritionistHubTracker } from '../hooks'
import { pendingEhrAppointmentsSelector } from '../models/nutritionistHub.selectors'
import { AppointmentScheduling } from '../components/AppointmentScheduling'
import {
  getSchedulingInfoForCoachingKind,
  useCallSchedulingState,
} from '../components/NextCall/useCallSchedulingState'
import { EhrBookAppointmentMutation } from '../graphql/bookAppointment.generated'
import { EhrAppointmentQuery } from '../graphql/ehrAppointment.generated'

const DEFAULT_TITLE = 'Video Call'
const CROSS_KIND_RECURRENCE_WARNING =
  'You already have another recurrence on this day. Do you still want to continue?'

export const CallSchedulingContainer = () => {
  /**
   * Hooks
   */
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const authorizedNavigation = useNavigation<StackNavigationProp<CommonAuthorizedStackParamList>>()
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const showSnack = useSnack()
  const showActionBottomSheet = useActionBottomSheet()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const track = useNutritionistHubTracker()
  const bookingRef = useRef(false)

  /**
   * Selectors
   */
  const user = useSelector(userSelector)
  const appointmentKinds = useSelector(appointmentKindsSelector)
  const pendingAppointments = useSelector(pendingEhrAppointmentsSelector)

  /**
   * Feature Flags
   */
  const videoCallsSchedulingOptionsEnabled = useFeatureFlag(Feature.VideoCallSchedulingOptions)
  const unifiedBookingFlows = useFeatureFlag(Feature.UnifiedAppointmentBookingFlow)

  /**
   * Route params
   */
  const route = useRoute<
    | RouteProp<AppStackParamList, 'CallScheduling'>
    | RouteProp<OnboardingStackParamsList, 'CallScheduling'>
  >()
  const {
    appointmentId: rescheduleAppointmentId,
    appointmentKind: appointmentKindFromParams,
    allowBackNavigation,
    pendingAppointmentId,
    coachProfile,
    errorMessage,
    feeProduct,
    switchingCoach,
    product,
    showCoachProfile,
    cadenceOptions: cadenceOptionsFromParams = [],
  } = route.params

  /**
   * States
   */
  const [prevErrorMessage, setPrevErrorMessage] = useState<string | undefined>(() => errorMessage)
  const [selectedSlot, setSelectedSlot] = useState<EhrSlot | null>(null)
  const [appointmentKind, setAppointmentKind] = useState(appointmentKindFromParams)
  const [cadence, setCadence] = useState(EhrAppointmentRecurrenceCadence.OneTime)

  /**
   * Custom Hooks
   */
  const { schedulingInfo, loading, error } = useCallSchedulingState()
  const {
    availableDates,
    crossKindRecurrenceDates,
    hasFetchedAnyMonth,
    selectedDate,
    selectedMonth,
    loading: loadingDates,
    setSelectedDate,
    error: loadingDatesError,
    reload,
    onMonthChange,
  } = useEhrAvailableDates(
    appointmentKind,
    cadence,
    rescheduleAppointmentId,
    coachProfile?.coachId,
    switchingCoach,
  )

  const { availableSlots, loading: fetchingSlots } = useEhrAvailableSlots(
    appointmentKind!,
    cadence,
    selectedDate,
    coachProfile?.coachId,
  )

  /**
   * Constants & Memos
   */
  const nextScreen = 'nextScreen' in route.params ? route.params.nextScreen : undefined
  const insuranceKind = 'insuranceKind' in route.params ? route.params.insuranceKind : undefined
  const pendingAppointment = pendingAppointments.find(
    (appointment) => appointment.id === pendingAppointmentId,
  )
  const appointmentKindConfig = appointmentKinds.find((config) => config.key === appointmentKind)

  const coachingSchedulingInfo = useMemo(() => {
    const coachingKindInfo = appointmentKindConfig?.coachingKind
    const coachingKind = coachingKindInfo?.key || CoachesCoachingKind.Mnt

    return getSchedulingInfoForCoachingKind(schedulingInfo, coachingKind) ?? schedulingInfo?.mnt
  }, [schedulingInfo, appointmentKindConfig])
  const coachingSchedulingInfoCadenceOptions = coachingSchedulingInfo?.cadenceOptions
  const coachingSchedulingInfoOptions = coachingSchedulingInfo?.options

  const attendanceChargeKind = useMemo(() => {
    const attendances = pendingAppointment?.attendances
    const pendingAttendance = attendances?.find((attendance) => attendance.user.id === user?.id)
    if (pendingAttendance) {
      return pendingAttendance.chargeKind
    }

    return coachingSchedulingInfo?.chargeKind ?? EhrAppointmentAttendanceChargeKind.BsPaid
  }, [coachingSchedulingInfo, pendingAppointment, user?.id])

  const cadenceOptions = useMemo(() => {
    if (coachingSchedulingInfoCadenceOptions && coachingSchedulingInfoCadenceOptions.length > 0) {
      return coachingSchedulingInfoCadenceOptions.map((option) => {
        return {
          value: option.key,
          text: option.label,
        }
      })
    }

    return cadenceOptionsFromParams
  }, [cadenceOptionsFromParams, coachingSchedulingInfoCadenceOptions])

  const hasSchedulingOptions = useMemo(() => {
    if (!videoCallsSchedulingOptionsEnabled) {
      return false
    }

    const options = coachingSchedulingInfoOptions || []

    return options.length > 1 || cadenceOptions.length > 1
  }, [videoCallsSchedulingOptionsEnabled, coachingSchedulingInfoOptions, cadenceOptions])

  const selectedOption = coachingSchedulingInfoOptions?.find(
    (option) => option.appointmentKind === appointmentKind,
  )
  const selectedPayAsYouGoProduct = selectedOption?.payAsYouGoProduct ?? product
  const crossKindRecurrenceDateSet = useMemo(() => new Set(crossKindRecurrenceDates), [
    crossKindRecurrenceDates,
  ])

  const showSchedulingOptions =
    switchingCoach || (hasSchedulingOptions && !rescheduleAppointmentId && !pendingAppointmentId)
  const shouldShowReviewScreen =
    unifiedBookingFlows &&
    !pendingAppointmentId &&
    !nextScreen &&
    (attendanceChargeKind === EhrAppointmentAttendanceChargeKind.BsSelfPay ||
      attendanceChargeKind === EhrAppointmentAttendanceChargeKind.BsInsuranceCovered ||
      attendanceChargeKind === EhrAppointmentAttendanceChargeKind.BsAllowanceCovered)

  /**
   *  Lazy loads
   */
  useLazyScreenPreload('CoachSwitchSummaryScreen', { shouldPreload: switchingCoach })
  useLazyScreenPreload('CallSchedulingReviewScreen', {
    shouldPreload: shouldShowReviewScreen,
  })

  /**
   * Effects
   */
  useEffect(() => {
    if (user && !user.paymentMethod && appointmentKind === EhrAppointmentKind.Initial_60) {
      authorizedNavigation.navigate('CreatePaymentMethod')
    }
  }, [user, appointmentKind, authorizedNavigation])

  useFocusEffect(
    useCallback(() => {
      if (errorMessage === prevErrorMessage) {
        return
      }
      if (!prevErrorMessage && errorMessage) {
        // Reset the scheduler state if we pop back to this screen with an error message,
        // e.g. when the selected slot is no longer available during review/confirmation.
        setSelectedSlot(null)
        reload()
      }
      setPrevErrorMessage(errorMessage)
    }, [errorMessage, prevErrorMessage, reload]),
  )

  useEffect(
    () => () => {
      if (bookingRef.current) {
        disableLoader()
        bookingRef.current = false
      }
    },
    [disableLoader],
  )

  /**
   * Functions
   */
  const handleTitle = () => {
    if (switchingCoach) {
      return 'Schedule New Call'
    }

    if (rescheduleAppointmentId) {
      return 'Reschedule Appointment'
    }

    return `Schedule ${appointmentKindConfig?.title || DEFAULT_TITLE}`
  }

  const showCrossKindRecurrenceWarning = useCallback(
    (onContinue: () => void) => {
      showActionBottomSheet({
        title: 'Continue booking?',
        body: CROSS_KIND_RECURRENCE_WARNING,
        primaryButton: {
          text: 'Continue',
          onPress: onContinue,
        },
        secondaryButton: {
          text: 'Go Back',
        },
      })
    },
    [showActionBottomSheet],
  )

  const handleDateSelection = useCallback(
    (date: string) => {
      if (selectedDate !== date) {
        setSelectedSlot(null)
      }

      setSelectedDate(date)
    },
    [selectedDate, setSelectedDate],
  )

  const rescheduleCall = () => {
    if (!selectedSlot || !rescheduleAppointmentId) {
      return
    }

    // User is doing a late reschedule
    // We will first charge the fee and then reschedule the appointment
    if (feeProduct && feeProduct.key === BillingProduct.VideoCallLateRescheduleFee) {
      navigation.navigate('FeePaymentScreen', {
        videoCallId: rescheduleAppointmentId,
        feeProduct,
        appointmentKind,
        selectedSlotDate: selectedSlot.time,
      })
      return
    }

    if (bookingRef.current) {
      return
    }

    bookingRef.current = true
    enableLoader()

    dispatch({
      type: 'nutritionistHub/rescheduleAppointment',
      payload: {
        date: selectedSlot.time,
        id: rescheduleAppointmentId,
      },
      success: () => {
        navigation.replace('Drawer', { screen: 'Dashboard', params: { screen: 'NutritionistHub' } })
        showSnack('Appointment rescheduled successfully', null, 'success')
      },
      failure: async (error: any) => {
        try {
          const appointment = await dispatchAsync<EhrAppointmentQuery['ehrAppointment']>({
            type: 'nutritionistHub/fetchEhrAppointment',
            payload: { id: rescheduleAppointmentId },
          })

          if (isAppointmentRescheduled(appointment, selectedSlot.time)) {
            navigation.replace('Drawer', {
              screen: 'Dashboard',
              params: { screen: 'NutritionistHub' },
            })
            showSnack('Appointment rescheduled successfully', null, 'success')
            return
          }
        } catch {
          // If the follow-up check fails, fall back to the original mutation error.
        }

        const errorMessage = error?.message || ErrorMessages.ServerError
        setSelectedSlot(null)
        showSnack(errorMessage, null, 'error')
      },
      complete: () => {
        disableLoader()
        bookingRef.current = false
      },
    })
  }

  const scheduleCall = () => {
    if (!selectedSlot) {
      return
    }

    if (shouldShowReviewScreen) {
      setPrevErrorMessage(undefined)
      navigation.setParams({ errorMessage: undefined })
      navigation.navigate('CallSchedulingReviewScreen', {
        appointmentKind,
        selectedSlot,
        product: selectedPayAsYouGoProduct,
        cadence,
        insuranceKind,
      })
      return
    }

    if (!user?.paymentMethod && appointmentKind === EhrAppointmentKind.Initial_60) {
      authorizedNavigation.navigate('CreatePaymentMethod')
      return
    }

    if (bookingRef.current) {
      return
    }

    bookingRef.current = true
    enableLoader()

    dispatch({
      type: 'nutritionistHub/bookAppointment',
      payload: {
        appointmentKind,
        pendingAppointmentId,
        timeZone: user?.timeZone,
        date: selectedSlot.time,
        coachId: selectedSlot.providerId,
        attendanceChargeKind,
        cadence,
        insuranceKind,
      },
      success: (response: EhrBookAppointmentMutation['ehrBookAppointment']) => {
        track(CustomEventTypes.NutritionistHubAppointmentBooked)
        dispatch({ type: 'nutritionistHub/fetchUpcomingEhrAppointments' })

        if (cadence !== EhrAppointmentRecurrenceCadence.OneTime) {
          dispatch({ type: 'nutritionistHub/fetchAppointmentRecurrence' })
        }

        if (!response.meetingAt) {
          return
        }

        if (nextScreen) {
          if (nextScreen.reset) {
            navigation.reset({
              index: 0,
              routes: [{ name: nextScreen.screen, params: nextScreen.params }] as any,
            })
            return
          }

          navigation.replace(nextScreen.screen as any, nextScreen.params)
          return
        }

        navigation.replace('CallSchedulingConfirmationScreen', { appointmentId: response.id })
      },
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        setSelectedSlot(null)
        showSnack(errorMessage, null, 'error', 'x', undefined, 'redesigned')
      },
      complete: () => {
        disableLoader()
        bookingRef.current = false
      },
    })
  }

  const onConfirmSlot = ({
    selectedSlot,
    appointmentKind,
    cadence,
  }: {
    selectedSlot: EhrSlot
    appointmentKind: EhrAppointmentKind
    cadence: EhrAppointmentRecurrenceCadence
  }) => {
    const selectedSlotDate = selectedDate || selectedSlot.time.split('T')[0]

    const continueBooking = () => {
      // User is on the process of switching the nutritionist
      // We want to redirect them to a confirmation/summary screen before
      // doing the changes
      if (switchingCoach) {
        navigation.navigate('CoachSwitchSummaryScreen', {
          userInitiated: true,
          appointmentKind,
          cadence,
          selectedSlot,
          coachProfile,
        })
        return
      }

      rescheduleAppointmentId ? rescheduleCall() : scheduleCall()
    }

    if (selectedSlotDate && crossKindRecurrenceDateSet.has(selectedSlotDate)) {
      showCrossKindRecurrenceWarning(continueBooking)
      return
    }

    continueBooking()
  }

  return (
    <NavigationContainer
      title={handleTitle()}
      allowBackNavigation={allowBackNavigation}
      navigationBarProps={{ titleStyle: styles.navigationTitle }}
    >
      <AppointmentScheduling
        coachProfile={coachProfile}
        errorMessage={errorMessage}
        onConfirmSlot={onConfirmSlot}
        selectedSlot={selectedSlot}
        setSelectedSlot={setSelectedSlot}
        appointmentKind={appointmentKind}
        setAppointmentKind={setAppointmentKind}
        cadence={cadence}
        setCadence={setCadence}
        selectedDate={selectedDate}
        selectedMonth={selectedMonth}
        setSelectedDate={handleDateSelection}
        onMonthChange={onMonthChange}
        hasFetchedAnyMonth={hasFetchedAnyMonth}
        loadingDates={loading || loadingDates}
        loadingDatesError={!!error || loadingDatesError}
        availableDates={availableDates}
        availableSlots={availableSlots}
        fetchingSlots={fetchingSlots}
        showSchedulingOptions={showSchedulingOptions}
        showCoachProfile={showCoachProfile}
        cadenceOptions={cadenceOptions}
        timeZone={user?.timeZone}
      />
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  navigationTitle: {
    maxWidth: '90%',
  },
})
